import { RefElement } from 'widgets/toolbox/RefElement';

/**
 * @param BasicForm Base widget for extending
 * @returns NoSearchResultsForm widget
 */
function NoSearchResultsFormClassCreator(BasicForm: import('widgets/forms/BasicForm').TBasicForm) {
    /**
     * @category widgets
     * @subcategory search
     * @class NoSearchResultsForm
     * @augments BasicForm
     * @classdesc NoSearchResultsForm widget component with next features:
     * 1. This is simplified form with no validation rules applied
     * 2. Has an ability to clear user input by pressing button
     * @property {string} data-widget - Widget name "noSearchResultsForm"
     * @property {string} data-classes-clear-enabled - class to toggle on clear input button
     * @example <caption>Example of NoSearchResultsForm widget usage</caption>
     * <form
     *     role="search"
     *     method="get"
     *     action="${URLUtils.url('Search-Show')}"
     *     name="simpleSearch"
     *     class="b-search_input"
     *     data-widget="noSearchResultsForm"
     *     data-event-submit="handleSubmit"
     * >
     *     <button
     *         class="b-search_input-submit"
     *         data-ref="submit"
     *         type="submit"
     *         enterkeyhint="search"
     *         title="${Resource.msg('header.search.submit', 'search', null)}"
     *         tabindex="0"
     *     >
     *         <isinclude template="/common/icons/standalone/search" />
     *     </button>
     *     <input
     *         aria-label="${Resource.msg('results.inputLabel.wai', 'search', null)}"
     *         data-ref="input"
     *         data-event-input="handleInput"
     *         id="noresult-search-input"
     *         class="b-search_input-input"
     *         type="search"
     *         name="q"
     *         value="${pdict.productSearch.searchKeywords || ''}"
     *         placeholder="${Resource.msg('header.search.placeholder', 'search', null)}"
     *         autocapitalize="off"
     *         autocomplete="off"
     *         spellcheck="false"
     *         maxlength="50"
     *     />
     *     <button
     *         aria-hidden="true"
     *         class="b-search_input-clear ${pdict.productSearch.searchKeywords ? 'm-visible' : ''}"
     *         data-ref="clearButton"
     *         data-event-click="clearInput"
     *         type="button"
     *         title="${Resource.msg('header.search.clear', 'search', null)}"
     *         tabindex="-1"
     *     >
     *         <isinclude template="/common/icons/standalone/clear" />
     *     </button>
     * </form>
     */
    class NoSearchResultsForm extends BasicForm {
        prefs() {
            return {
                classesClearEnabled: 'm-visible',
                ...super.prefs()
            };
        }

        /**
         * @description Submit form simplified handler
         * @param _el event source element
         * @param event event instance
         */
        handleSubmit(_el: RefElement, event: Event) {
            if (!this.ref('input').val()) {
                event.preventDefault();
            }
        }

        /**
         * @description Handle input event into the form field
         * @listens dom#input
         */
        handleInput() {
            this.toggleClearButton(!!this.ref('input').val());
        }

        /**
         * @description Toggle Clear Button based on input value
         * @param isInputHasValue - indicates if input has a value
         */
        toggleClearButton(isInputHasValue: boolean) {
            this.ref('clearButton').toggleClass(this.prefs().classesClearEnabled, isInputHasValue);
        }

        /**
         * @description Clear input field
         */
        clearInput() {
            const searchInput = this.ref('input');

            searchInput.val('');

            const searchInputField = searchInput.get();

            if (searchInputField) {
                searchInputField.focus();
            }

            this.toggleClearButton(false);
        }
    }

    return NoSearchResultsForm;
}

export type TNoSearchResultsForm = ReturnType<typeof NoSearchResultsFormClassCreator>;

export type TNoSearchResultsFormInstance = InstanceType<TNoSearchResultsForm>;

export default NoSearchResultsFormClassCreator;
